import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from "@reduxjs/toolkit/query/react";
import { t } from "i18next";
import {
  BaseQueryApi,
  QueryReturnValue,
} from "@reduxjs/toolkit/dist/query/baseQueryTypes";
import toast from "react-hot-toast/headless";
import { AdminState } from "src/apps/admin/stores/AdminStore";
import { msalInstance } from "@unleashed-admin/utils/authentication";
import { setAccessToken, signOut } from "@unleashed-admin/stores/AuthSlice";
import { GetErrorMessage, isFetchArgs } from "@unleashed-admin/utils/apiUtils";

const baseQuery = fetchBaseQuery({
  baseUrl: `${import.meta.env.VITE_PRINTER_API_URL}`,
  prepareHeaders: (headers, { getState }) => {
    const {
      auth: { accessToken },
    } = getState() as AdminState;
    if (accessToken) {
      headers.set("Authorization", `Bearer ${accessToken}`);
    }
    return headers;
  },
});

const isPlainTextResponse = (
  queryResult: QueryReturnValue<
    unknown,
    FetchBaseQueryError,
    FetchBaseQueryMeta
  >
) =>
  queryResult.meta?.response?.headers
    .get("content-type")
    ?.includes("text/plain");

const refreshTokenQuery = (
  args: string | FetchArgs,
  api: BaseQueryApi,
  // fetchBaseQuery requires {} as the param
  extraOptions: {}
) => {
  return async () => {
    let result = await baseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 401) {
      const authResult = await msalInstance.acquireTokenSilent({
        scopes: [
          "openid",
          "offline_access",
          import.meta.env.VITE_AZURE_B2C_APP_ID,
        ],
      });

      if (authResult.accessToken) {
        api.dispatch(setAccessToken(authResult.accessToken));
        result = await baseQuery(args, api, extraOptions);
      } else {
        msalInstance.logoutRedirect().then(() => api.dispatch(signOut()));
      }
    }

    return result;
  };
};

const printerBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const query = refreshTokenQuery(args, api, extraOptions);

  if (!isFetchArgs(args) || args.method === "GET") {
    const result = await query();

    if (result.error) {
      toast.error(GetErrorMessage(result.error, isPlainTextResponse(result)));
    }

    return result;
  } else {
    const promise = async () => {
      const result = await query();

      if (result.error) {
        throw GetErrorMessage(result.error, isPlainTextResponse(result));
      }

      return result;
    };

    return toast.promise(promise(), {
      loading:
        args.method === "DELETE" ? `${t`Deleting`}...` : `${t`Saving`}...`,
      success: `${t`Success`}!`,
      error: (e) => e,
    });
  }
};

export const PosBasePrinterApi = createApi({
  reducerPath: "posBasePrinterApi",
  baseQuery: printerBaseQuery,
  endpoints: () => ({}),
});
